import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  CheckCircle2,
  XCircle,
  AlertTriangle,
  Info,
  X,
} from 'lucide-react';
import { useToastStore, Toast, ToastType } from '../store/toastStore';
import { useThemeStore } from '../store/themeStore';

const ICON_MAP: Record<ToastType, React.ReactNode> = {
  success: <CheckCircle2 size={20} />,
  error: <XCircle size={20} />,
  warning: <AlertTriangle size={20} />,
  info: <Info size={20} />,
};

const COLOR_MAP: Record<ToastType, { bg: string; border: string; icon: string; darkBg: string; darkBorder: string }> = {
  success: {
    bg: 'bg-emerald-50',
    border: 'border-emerald-200',
    icon: 'text-emerald-500',
    darkBg: 'bg-emerald-500/10',
    darkBorder: 'border-emerald-500/30',
  },
  error: {
    bg: 'bg-red-50',
    border: 'border-red-200',
    icon: 'text-red-500',
    darkBg: 'bg-red-500/10',
    darkBorder: 'border-red-500/30',
  },
  warning: {
    bg: 'bg-amber-50',
    border: 'border-amber-200',
    icon: 'text-amber-500',
    darkBg: 'bg-amber-500/10',
    darkBorder: 'border-amber-500/30',
  },
  info: {
    bg: 'bg-blue-50',
    border: 'border-blue-200',
    icon: 'text-blue-500',
    darkBg: 'bg-blue-500/10',
    darkBorder: 'border-blue-500/30',
  },
};

const ToastItem: React.FC<{ t: Toast; theme: string }> = ({ t, theme }) => {
  const removeToast = useToastStore((s) => s.removeToast);
  const colors = COLOR_MAP[t.type];
  const isDark = theme === 'dark';

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: -20, x: 20, scale: 0.9 }}
      animate={{ opacity: 1, y: 0, x: 0, scale: 1 }}
      exit={{ opacity: 0, x: 60, scale: 0.85 }}
      transition={{
        type: 'spring',
        damping: 22,
        stiffness: 300,
        mass: 0.8,
      }}
      className={`relative flex items-start gap-3 px-4 py-3.5 rounded-xl border shadow-lg backdrop-blur-sm min-w-[320px] max-w-[420px] cursor-pointer group ${
        isDark
          ? `${colors.darkBg} ${colors.darkBorder}`
          : `${colors.bg} ${colors.border}`
      }`}
      onClick={() => removeToast(t.id)}
      role="alert"
    >
      {/* Icon */}
      <div className={`flex-shrink-0 mt-0.5 ${colors.icon}`}>
        {ICON_MAP[t.type]}
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <p
          className={`text-sm font-semibold leading-5 ${
            isDark ? 'text-gpt-dark-text' : 'text-gpt-light-text'
          }`}
        >
          {t.title}
        </p>
        {t.message && (
          <p
            className={`text-xs mt-0.5 leading-4 ${
              isDark ? 'text-gpt-dark-text-secondary' : 'text-gpt-light-text-secondary'
            }`}
          >
            {t.message}
          </p>
        )}
      </div>

      {/* Close button */}
      <button
        onClick={(e) => {
          e.stopPropagation();
          removeToast(t.id);
        }}
        className={`flex-shrink-0 p-0.5 rounded-md opacity-0 group-hover:opacity-100 transition-opacity ${
          isDark
            ? 'hover:bg-white/10 text-gpt-dark-text-secondary'
            : 'hover:bg-black/5 text-gpt-light-text-secondary'
        }`}
      >
        <X size={14} />
      </button>

      {/* Auto-dismiss progress bar */}
      {t.duration && t.duration > 0 && (
        <motion.div
          initial={{ scaleX: 1 }}
          animate={{ scaleX: 0 }}
          transition={{ duration: t.duration / 1000, ease: 'linear' }}
          className={`absolute bottom-0 left-0 right-0 h-[2px] rounded-b-xl origin-left ${colors.icon.replace('text-', 'bg-')} opacity-40`}
        />
      )}
    </motion.div>
  );
};

const ToastContainer: React.FC = () => {
  const toasts = useToastStore((s) => s.toasts);
  const theme = useThemeStore((s) => s.theme);

  return (
    <div className="fixed top-4 right-4 z-[9999] flex flex-col gap-2 pointer-events-none">
      <AnimatePresence mode="popLayout">
        {toasts.map((t) => (
          <div key={t.id} className="pointer-events-auto">
            <ToastItem t={t} theme={theme} />
          </div>
        ))}
      </AnimatePresence>
    </div>
  );
};

export default ToastContainer;